// scripts/imageCompress.js
//
// Client-side image compression for driver-portal uploads. Phones produce
// 3–10 MB JPEGs which blow past our 8 MB server limit on slow LTE and mean
// the driver waits a minute per breakdown photo. This scales + re-encodes
// to keep upload size tight while staying readable in breakdown emails.
//
// Defaults: max 1600px on the longest side, JPEG quality 0.82. Typical
// result: ~200–500 KB from a stock iPhone 13 shot, no visible quality loss
// at email thumbnail size.
//
// Usage:
//   const small = await compressImage(file);           // same-file if non-image
//   const small = await compressImage(file, { maxPx: 2000, quality: 0.9 });
//
// Returns a File (same `name`, same `lastModified`) so FormData just works.
// If anything throws — old browser, HEIC without decode support, corrupt
// file — returns the original input untouched.

export async function compressImage(file, opts = {}) {
  if (!file || !file.type?.startsWith('image/')) return file;

  const maxPx    = opts.maxPx    ?? 1600;
  const quality  = opts.quality  ?? 0.82;
  const mimeOut  = opts.mime     ?? 'image/jpeg';

  try {
    const bmp = await loadBitmap(file);
    const w0  = bmp.width, h0 = bmp.height;
    if (!w0 || !h0) return file;

    // If already within bounds AND file is < ~600 KB, skip re-encoding —
    // saves a canvas pass and avoids quality loss on tiny images.
    if (Math.max(w0, h0) <= maxPx && file.size < 600 * 1024) return file;

    const scale = Math.min(1, maxPx / Math.max(w0, h0));
    const w = Math.round(w0 * scale);
    const h = Math.round(h0 * scale);

    const canvas = document.createElement('canvas');
    canvas.width  = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bmp, 0, 0, w, h);
    // Release the decoded bitmap if supported.
    if (typeof bmp.close === 'function') { try { bmp.close(); } catch (_) {} }

    const blob = await canvasToBlob(canvas, mimeOut, quality);
    if (!blob) return file;
    // If the re-encoded blob is somehow LARGER than the original (e.g. tiny
    // PNG icon we just re-encoded as JPEG), prefer the original.
    if (blob.size >= file.size) return file;

    const outName = renameToExt(file.name || 'photo', mimeOut === 'image/jpeg' ? 'jpg' : mimeOut.split('/')[1]);
    return new File([blob], outName, {
      type:         blob.type || mimeOut,
      lastModified: file.lastModified || Date.now(),
    });
  } catch (_) {
    // Any failure → return original so the submit still works.
    return file;
  }
}

// Try createImageBitmap first (fast + handles EXIF orientation in modern
// browsers). Fall back to <img> + URL.createObjectURL for Safari quirks.
async function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); }
    catch (_) { /* fall through to <img> */ }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => {
    if (canvas.toBlob) canvas.toBlob((b) => resolve(b), mime, quality);
    else {
      // Very old browsers: dataURL fallback.
      try {
        const dataUrl = canvas.toDataURL(mime, quality);
        const bstr = atob(dataUrl.split(',')[1]);
        const u8 = new Uint8Array(bstr.length);
        for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
        resolve(new Blob([u8], { type: mime }));
      } catch (_) { resolve(null); }
    }
  });
}

function renameToExt(name, ext) {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base}.${ext}`;
}

// Convenience: compress an array of Files in parallel, preserving order.
export async function compressImages(files, opts = {}) {
  if (!Array.isArray(files) || files.length === 0) return [];
  return await Promise.all(files.map((f) => compressImage(f, opts)));
}
